import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, Globe, ChevronDown, LogOut, User as UserIcon, Bookmark, FileText } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Header() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/jobs", label: t.nav.jobs },
    { to: "/services", label: t.nav.services },
    { to: "/about", label: t.nav.about },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-slate-200/60" data-testid="site-header">
      <div className="container-pad mx-auto max-w-[1400px] flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link" onClick={() => setOpen(false)}>
          <img src="/logo.jpg" alt="ENCES Global" className="w-11 h-11 rounded-xl object-cover ring-2 ring-white shadow-sm" />
          <div className="leading-tight">
            <div className="text-navy font-extrabold tracking-tight text-lg">ENCES</div>
            <div className="text-[10px] tracking-[0.25em] text-slate-500 uppercase">Global</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "text-navy bg-soft" : "text-slate-600 hover:text-navy hover:bg-soft/60"}`}
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
            >{l.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-navy border border-slate-200 hover:border-navy transition-colors"
            data-testid="language-toggle"
            aria-label="Toggle language"
          >
            <Globe size={14} /> {lang.toUpperCase()}
          </button>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-slate-200 hover:border-navy" data-testid="user-menu-trigger">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-navy text-white text-xs font-bold">
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </span>
                  <ChevronDown size={14} className="text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="truncate">{user.name || user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="menu-saved">
                  <Link to="/dashboard/saved"><Bookmark className="mr-2 h-4 w-4" /> {t.nav.saved}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-applications">
                  <Link to="/dashboard/applications"><FileText className="mr-2 h-4 w-4" /> {t.nav.applications}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="menu-profile">
                  <Link to="/dashboard/profile"><UserIcon className="mr-2 h-4 w-4" /> {t.nav.profile}</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" /> {t.nav.logout}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden md:inline-flex text-sm font-semibold text-navy hover:text-brandred px-3" data-testid="nav-login">
              {t.nav.login}
            </Link>
          )}

          <Button asChild className="hidden md:inline-flex rounded-full bg-brandred hover:bg-red-700 px-6 h-11 font-semibold shadow-lg shadow-red-500/20" data-testid="nav-hire-talent">
            <Link to="/hire">{t.nav.hire}</Link>
          </Button>

          <button className="lg:hidden p-2 rounded-full hover:bg-soft" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="container-pad py-4 flex flex-col gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className={`px-3 py-3 rounded-xl text-base font-semibold ${loc.pathname === l.to ? "bg-soft text-navy" : "text-slate-600"}`}>{l.label}</Link>
            ))}
            <button onClick={() => { toggle(); }} className="px-3 py-3 rounded-xl text-left text-base font-semibold text-slate-600" data-testid="mobile-language-toggle">
              <Globe size={14} className="inline mr-1" /> {lang === "en" ? "Español" : "English"}
            </button>
            {user ? (
              <>
                <Link to="/dashboard/saved" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-slate-600">{t.nav.saved}</Link>
                <Link to="/dashboard/applications" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-slate-600">{t.nav.applications}</Link>
                <Link to="/dashboard/profile" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-slate-600">{t.nav.profile}</Link>
                <button onClick={() => { logout(); setOpen(false); }} className="px-3 py-3 rounded-xl text-left text-slate-600">{t.nav.logout}</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-3 rounded-xl text-slate-600">{t.nav.login}</Link>
            )}
            <Link to="/hire" onClick={() => setOpen(false)} className="mt-2 px-4 py-3 rounded-full bg-brandred text-white text-center font-semibold">{t.nav.hire}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
